import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ApiError, errorMessage, get, put } from '../api/client';
import type { SiteText } from '../api/types';
import { Field, PageHead, Spinner, useFeedback } from '../components/ui';
import { useDraft, useNavigationGuard, useUnloadGuard } from '../hooks';
import { tk, useT } from '../i18n';
import { SaveBar } from './model/SaveBar';

/** Qué pone el sitio en cada {variable} de un texto (se traduce con t() al mostrarlo; los ejemplos son del sitio, en español). */
const PLACEHOLDER_HELP: Record<string, string> = {
  cantidad: tk('número de motos visibles en letras («cinco»)'),
  Cantidad: tk('lo mismo, con mayúscula («Cinco»)'),
  modelos: tk('los nombres de las motos visibles («Aspen 150, Rush 150 y Way 150»)'),
  familias: tk('las familias de motos («Aspen, Metro, Rush y Way»)'),
};

export function Texts() {
  const { t } = useT();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const { data, isPending } = useQuery({ queryKey: ['texts'], queryFn: () => get<{ items: SiteText[] }>('/admin/texts') });
  const initial = useMemo(() => Object.fromEntries((data?.items ?? []).map((t) => [t.key, t.value])), [data]);
  const { draft: values, setDraft: setValues, dirty, reset, markSaved } = useDraft<Record<string, string>>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const changed = Object.keys(values).filter((k) => values[k] !== initial[k]);
  useUnloadGuard(dirty);
  useNavigationGuard(dirty);

  const groups = useMemo(() => {
    const map = new Map<string, SiteText[]>();
    for (const t of data?.items ?? []) map.set(t.group, [...(map.get(t.group) ?? []), t]);
    return [...map.entries()];
  }, [data]);

  const save = async () => {
    const submitted = values;
    setBusy(true);
    setError(null);
    try {
      const res = await put<{ items: SiteText[] }>('/admin/texts', { values: Object.fromEntries(changed.map((k) => [k, values[k]!])) });
      qc.setQueryData(['texts'], res);
      markSaved(submitted);
      toast(changed.length === 1 ? t('Texto guardado') : t('{n} textos guardados', { n: changed.length }));
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        kicker={t('Contenido')}
        title={t('Textos del sitio')}
        description={t('Los textos de la portada, el catálogo y la garantía, y lo que muestra Google de cada página. El diseño está medido al milímetro: respeta el largo máximo.')}
      />
      {isPending ? (
        <Spinner />
      ) : (
        <div className="stack">
          {groups.map(([group, items]) => (
            <section key={group} className="panel panel-pad stack">
              <h2 className="section-title">{group}</h2>
              {items.map((text) => {
                const value = values[text.key] ?? '';
                return (
                  <Field
                    key={text.key}
                    label={text.label}
                    error={error?.field(text.key)}
                    count={{ value, max: text.maxLength }}
                    hint={
                      <span className="row row-wrap" style={{ gap: 6 }}>
                        {text.placeholders.map((p) => {
                          // Las {variables} que la API agregue sin ayuda aquí se muestran sin explicación (como antes).
                          const help = PLACEHOLDER_HELP[p] && t(PLACEHOLDER_HELP[p]);
                          return (
                            <span key={p} className="badge info" title={help}>
                              {`{${p}}`} = {help}
                            </span>
                          );
                        })}
                        {value !== text.default && (
                          <button className="btn btn-ghost btn-sm" type="button" onClick={() => setValues((v) => ({ ...v, [text.key]: text.default }))}>
                            {t('Volver al texto original')}
                          </button>
                        )}
                        {text.updatedBy && <span className="faint">{t('Editado por {name}', { name: text.updatedBy })}</span>}
                      </span>
                    }
                  >
                    {(id) =>
                      text.multiline ? (
                        <textarea
                          id={id}
                          className="textarea"
                          rows={Math.min(6, Math.ceil(text.maxLength / 90))}
                          value={value}
                          onChange={(e) => setValues((v) => ({ ...v, [text.key]: e.target.value }))}
                        />
                      ) : (
                        <input id={id} className="input" value={value} onChange={(e) => setValues((v) => ({ ...v, [text.key]: e.target.value }))} />
                      )
                    }
                  </Field>
                );
              })}
            </section>
          ))}
          <SaveBar dirty={dirty} busy={busy} error={error} onSave={save} onReset={reset} />
        </div>
      )}
    </>
  );
}
